/**
 * Excel Export Utility (TASK-2026-062)
 *
 * Generates Excel files from simulator data using the xlsx library.
 * Supports multiple sheets for different data views.
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export interface ExcelExportData {
  clientName: string;
  generatedAt: Date;
  market?: {
    totalValue: number;
    currency: string;
    growthRate: number;
    topBrands: Array<{ name: string; share: number; volume?: number }>;
  };
  portfolio?: {
    skuCount: number;
    hhi: number;
    products: Array<{
      sku: string;
      name: string;
      brand: string;
      price: number;
      volume?: number;
      share?: number;
    }>;
  };
  pricing?: {
    scenarios: Array<{
      name: string;
      priceChange: number;
      volumeImpact: number;
      revenueImpact: number;
      marginImpact?: number;
    }>;
  };
  opportunities?: Array<{
    type: string;
    title: string;
    impact: string;
    priority: 'high' | 'medium' | 'low';
    recommendation: string;
  }>;
}

export interface ExcelExportOptions {
  filename?: string;
  includeMarket?: boolean;
  includePortfolio?: boolean;
  includePricing?: boolean;
  includeOpportunities?: boolean;
}

/**
 * Export simulator data to Excel workbook
 */
export function exportToExcel(
  data: ExcelExportData,
  options: ExcelExportOptions = {}
): void {
  const {
    filename = `${data.clientName.toLowerCase().replace(/\s+/g, '-')}-export`,
    includeMarket = true,
    includePortfolio = true,
    includePricing = true,
    includeOpportunities = true,
  } = options;

  const workbook = XLSX.utils.book_new();

  // Summary Sheet
  const summaryData = [
    ['CPG Simulator Export'],
    [],
    ['Client', data.clientName],
    ['Generated', data.generatedAt.toLocaleString()],
    [],
    ['Key Metrics'],
    ['Market Value', data.market ? `${data.market.currency} ${data.market.totalValue.toLocaleString()}` : 'N/A'],
    ['Growth Rate', data.market ? `${(data.market.growthRate * 100).toFixed(1)}%` : 'N/A'],
    ['SKUs Active', data.portfolio?.skuCount || 'N/A'],
    ['Portfolio HHI', data.portfolio ? `${(data.portfolio.hhi * 100).toFixed(1)}%` : 'N/A'],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [{ wch: 20 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Market Sheet
  if (includeMarket && data.market?.topBrands) {
    const marketData = [
      ['Brand', 'Share (%)', 'Volume'],
      ...data.market.topBrands.map(b => [
        b.name,
        (b.share * 100).toFixed(1),
        b.volume || '',
      ]),
    ];
    const marketSheet = XLSX.utils.aoa_to_sheet(marketData);
    marketSheet['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(workbook, marketSheet, 'Market');
  }

  // Portfolio Sheet
  if (includePortfolio && data.portfolio?.products) {
    const portfolioData = [
      ['SKU', 'Name', 'Brand', 'Price', 'Volume', 'Share (%)'],
      ...data.portfolio.products.map(p => [
        p.sku,
        p.name,
        p.brand,
        p.price,
        p.volume || '',
        p.share ? (p.share * 100).toFixed(1) : '',
      ]),
    ];
    const portfolioSheet = XLSX.utils.aoa_to_sheet(portfolioData);
    portfolioSheet['!cols'] = [
      { wch: 15 },
      { wch: 30 },
      { wch: 20 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(workbook, portfolioSheet, 'Portfolio');
  }

  // Pricing Scenarios Sheet
  if (includePricing && data.pricing?.scenarios) {
    const pricingData = [
      ['Scenario', 'Price Change (%)', 'Volume Impact (%)', 'Revenue Impact (%)', 'Margin Impact (%)'],
      ...data.pricing.scenarios.map(s => [
        s.name,
        (s.priceChange * 100).toFixed(1),
        (s.volumeImpact * 100).toFixed(1),
        (s.revenueImpact * 100).toFixed(1),
        s.marginImpact ? (s.marginImpact * 100).toFixed(1) : '',
      ]),
    ];
    const pricingSheet = XLSX.utils.aoa_to_sheet(pricingData);
    pricingSheet['!cols'] = [
      { wch: 25 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(workbook, pricingSheet, 'Pricing Scenarios');
  }

  // Opportunities Sheet
  if (includeOpportunities && data.opportunities) {
    const opportunitiesData = [
      ['Type', 'Title', 'Impact', 'Priority', 'Recommendation'],
      ...data.opportunities.map(o => [
        o.type,
        o.title,
        o.impact,
        o.priority.toUpperCase(),
        o.recommendation,
      ]),
    ];
    const oppsSheet = XLSX.utils.aoa_to_sheet(opportunitiesData);
    oppsSheet['!cols'] = [
      { wch: 15 },
      { wch: 30 },
      { wch: 20 },
      { wch: 10 },
      { wch: 50 },
    ];
    XLSX.utils.book_append_sheet(workbook, oppsSheet, 'Opportunities');
  }

  // Generate and download
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${filename}.xlsx`);
}

/**
 * Export raw data array to Excel (generic utility)
 */
export function exportArrayToExcel<T extends Record<string, unknown>>(
  data: T[],
  sheetName: string,
  filename: string
): void {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${filename}.xlsx`);
}
